import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { SimpleModal } from './SimpleModal';

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
  phoneNumber: string;
  expectedCode: string;
  onResendCode: () => Promise<void>;
  isResending?: boolean;
  /** When true, auto-fills the code for demo purposes */
  autoFillCode?: boolean;
}

export const PhoneVerificationModal = ({
  isOpen,
  onClose,
  onVerified,
  phoneNumber,
  expectedCode,
  onResendCode,
  isResending = false,
  autoFillCode = false,
}: PhoneVerificationModalProps) => {
  const [code, setCode] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [attemptCount, setAttemptCount] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const { phoneVerification } = useEventTracking();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setResendSuccess(false);
      setIsVerifying(false);
      setAttemptCount(0);
      setResendCount(0);
      phoneVerification.modalOpened();

      // Auto-fill code for demo mode, otherwise reset
      if (autoFillCode && expectedCode.length === 4) {
        setCode(expectedCode.split(''));
      } else {
        setCode(['', '', '', '']);
        // Focus the first input after a short delay
        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 100);
      }
    }
  }, [isOpen, phoneVerification, autoFillCode, expectedCode]);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle backspace - go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    // Only handle 4-digit paste
    if (/^\d{4}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setCode(digits);
      setError('');
      inputRefs.current[3]?.focus();
    }
  };

  const handleVerify = () => {
    const enteredCode = code.join('');

    if (enteredCode.length !== 4) {
      setError('Please enter all 4 digits');
      return;
    }

    setIsVerifying(true);
    const currentAttempt = attemptCount + 1;
    setAttemptCount(currentAttempt);

    // Compare with expected code
    const isValid = enteredCode === expectedCode;
    phoneVerification.submitted({ is_valid: isValid, attempt_number: currentAttempt });

    if (isValid) {
      phoneVerification.completed({ attempts: currentAttempt });
      onVerified();
    } else {
      phoneVerification.failed({ attempt_number: currentAttempt });
      setError('Invalid code. Please try again.');
      setCode(['', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsVerifying(false);
    }
  };


  const handleResend = async () => {
    setError('');
    setResendSuccess(false);
    const newResendCount = resendCount + 1;
    setResendCount(newResendCount);
    phoneVerification.resendClicked({ resend_count: newResendCount });

    try {
      await onResendCode();
      setResendSuccess(true);
      setCode(['', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch {
      setError('Failed to resend code. Please try again.');
    }
  };

  // Format phone number for display (mask middle digits)
  const formatPhoneForDisplay = (phone: string) => {
    // Remove non-digits for calculation
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) {
      const last4 = digits.slice(-4);
      return `***-***-${last4}`;
    }
    return phone;
  };

  const setInputRef = (index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  };

  return (
    <SimpleModal isOpen={isOpen} onClose={onClose}>
      <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Verify Your Phone
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We sent a 4-digit code to {formatPhoneForDisplay(phoneNumber)}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            A new code has been sent to your phone.
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{justifyContent: "center",  mb: 3 }}>
          {code.map((digit, index) => (
            <TextField
              key={index}
              inputRef={setInputRef(index)}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              sx={{
                width: 56,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              disabled={isVerifying}
             slotProps={{ htmlInput: {
                maxLength: 1,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  padding: '12px',
                },
                inputMode: 'numeric',
                'aria-label': `Digit ${index + 1} of verification code`,
              } }}/>
          ))}
        </Stack>

        <Stack spacing={2}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleVerify}
            disabled={code.join('').length !== 4 || isVerifying}
            sx={{ py: 1.5 }}
          >
            {isVerifying ? <CircularProgress size={24} /> : 'Verify'}
          </Button>

          <Button
            variant="text"
            onClick={handleResend}
            disabled={isResending}
            sx={{ textTransform: 'none' }}
          >
            {isResending ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Didn&apos;t receive a code? Resend
          </Button>

          <Typography variant="body2" color="text.secondary">
            Having trouble?{' '}
            <Typography
              component="a"
              variant="body2"
              href="mailto:support@jotpsych.com?subject=Phone%20Verification%20Issue"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Contact support
            </Typography>
          </Typography>
        </Stack>
      </Box>
    </SimpleModal>
  );
};
